// ForecastFields.tsx — the Prognose scenario form fields, grouped into collapsible
// <details> sections (Sparplan / Fonds & Steuer default-open, Rendite & Kosten closed)
// so the form scales as more options get exposed without becoming one long scroll.
import { useId } from 'preact/hooks';
import { ExtraContributionsEditor } from './ExtraContributionsEditor.js';
import { FundPicker } from './FundPicker.js';
import { NumberField } from './NumberField.js';
import { ToggleField } from './ToggleField.js';
import { useExplain } from './Hint.js';
import { applyFundToForecastForm, finalMonthlyEurAfterStepUp, type ForecastFormState } from '../lib/scenario.js';
import type { FundInfo } from '../lib/types.js';
import { formatCents } from '../lib/format.js';
import { eurToCents } from '../lib/convert.js';

export interface ForecastFieldsProps {
  form: ForecastFormState;
  onChange: (form: ForecastFormState) => void;
  funds: FundInfo[];
  fundsLoading?: boolean | undefined;
  fundsError?: string | null | undefined;
}

export function ForecastFields({ form, onChange, funds, fundsLoading, fundsError }: ForecastFieldsProps) {
  const patch = (p: Partial<ForecastFormState>) => onChange({ ...form, ...p });
  const startDateId = useId();
  const showExplanations = useExplain();

  return (
    <div>
      <details class="details-section" open>
        <summary>Sparplan</summary>
        <div class="section-body">
          <div class="field">
            <label for={startDateId}>Startdatum</label>
            <input
              id={startDateId}
              type="date"
              value={form.startDate}
              onInput={(e) => patch({ startDate: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>

          <NumberField
            label="Einmalerlag"
            unit="€"
            value={form.lumpSumEur}
            onChange={(v) => patch({ lumpSumEur: v })}
            min={0}
            max={200000}
            step={100}
            sliderScale="money"
          />

          <NumberField
            label="Monatliche Rate"
            unit="€"
            value={form.monthlyEur}
            onChange={(v) => patch({ monthlyEur: v })}
            min={0}
            max={3000}
            step={10}
          />

          <NumberField
            label="Sparplan-Dynamik (jährl. Erhöhung)"
            unit="%"
            value={form.contributionStepUpPercent}
            onChange={(v) => patch({ contributionStepUpPercent: v })}
            min={0}
            max={20}
            step={0.1}
            sliderMax={10}
            sliderStep={0.5}
            hint={
              form.contributionStepUpPercent > 0 && form.monthlyEur > 0
                ? `Endrate nach Dynamik: ${formatCents(eurToCents(finalMonthlyEurAfterStepUp(form)))}/Monat`
                : showExplanations
                  ? 'Erhöht die monatliche Rate jedes Jahr ab Sparplanbeginn automatisch.'
                  : undefined
            }
          />

          <NumberField
            label="Ausführungsgebühr"
            unit="€ / Ausführung"
            value={form.executionFeeEur}
            onChange={(v) => patch({ executionFeeEur: v })}
            min={0}
            max={20}
            step={0.1}
          />

          <NumberField
            label="Laufzeit"
            unit="Jahre"
            value={form.years}
            onChange={(v) => patch({ years: v })}
            min={1}
            max={40}
            step={1}
          />

          <ToggleField
            label="Verkauf am Ende"
            checked={form.sellAtEnd}
            onChange={(v) => patch({ sellAtEnd: v })}
            hint={
              form.withdrawalEnabled
                ? 'Wird ignoriert, solange die Entnahmephase aktiv ist (siehe unten).'
                : undefined
            }
          />

          <ExtraContributionsEditor
            startDate={form.startDate}
            rows={form.extraContributions}
            onChange={(extraContributions) => patch({ extraContributions })}
          />
        </div>
      </details>

      <details class="details-section">
        <summary>Entnahmeplan</summary>
        <div class="section-body">
          <ToggleField
            label="Entnahmephase aktivieren"
            checked={form.withdrawalEnabled}
            onChange={(v) => patch({ withdrawalEnabled: v })}
            hint={
              !showExplanations
                ? undefined
                : form.withdrawalEnabled
                  ? 'Nach der Ansparphase wird monatlich ein Bruttobetrag entnommen (Teilverkauf, gleitender Durchschnittspreis). „Verkauf am Ende" wird dabei ignoriert.'
                  : 'Simuliert eine monatliche Entnahme nach dem Sparplan (z. B. für die Pension) statt eines Verkaufs am Ende.'
            }
          />

          {form.withdrawalEnabled ? (
            <>
              <NumberField
                label="Monatliche Entnahme (brutto)"
                unit="€"
                value={form.withdrawalMonthlyGrossEur}
                onChange={(v) => patch({ withdrawalMonthlyGrossEur: v })}
                min={0}
                max={10000}
                step={50}
                sliderScale="money"
                hint={showExplanations ? 'Zielbetrag vor KESt. Reicht das Depot nicht mehr, wird nur der Restwert entnommen.' : undefined}
              />

              <NumberField
                label="Entnahmedauer"
                unit="Jahre"
                value={form.withdrawalYears}
                onChange={(v) => patch({ withdrawalYears: v })}
                min={1}
                max={60}
                step={1}
                hint={showExplanations ? 'Die Entnahme endet früher, sobald das Depot aufgebraucht ist.' : undefined}
              />
            </>
          ) : null}
        </div>
      </details>

      <details class="details-section" open>
        <summary>Fonds &amp; Steuer</summary>
        <div class="section-body">
          <FundPicker
            funds={funds}
            value={form.fundIsin}
            allowCustom
            loading={fundsLoading}
            error={fundsError}
            onChange={(isin) => {
              const fund = funds.find((f) => f.isin === isin) ?? null;
              onChange(applyFundToForecastForm(form, fund));
            }}
          />

          <div class="field">
            <span class="field-label">Fondstyp</span>
            <div class="segmented" role="group" aria-label="Fondstyp">
              <button
                type="button"
                class="chip"
                aria-pressed={form.kind === 'acc'}
                onClick={() => patch({ kind: 'acc' })}
              >
                Thesaurierend
              </button>
              <button
                type="button"
                class="chip"
                aria-pressed={form.kind === 'dist'}
                onClick={() => patch({ kind: 'dist' })}
              >
                Ausschüttend
              </button>
            </div>
          </div>

          <ToggleField
            label="Meldefonds"
            checked={form.meldefonds}
            onChange={(v) => patch({ meldefonds: v })}
            hint={form.meldefonds ? undefined : 'Nicht-Meldefonds: Pauschalbesteuerung (§ 186 Abs. 2 Z 3 InvFG) wird angewendet.'}
          />

          {form.kind === 'dist' ? (
            <div class="field">
              <span class="field-label">Ausschüttungsfrequenz</span>
              <div class="segmented" role="group" aria-label="Ausschüttungsfrequenz">
                <button
                  type="button"
                  class="chip"
                  aria-pressed={form.distributionFrequency === 'annual'}
                  onClick={() => patch({ distributionFrequency: 'annual' })}
                >
                  Jährlich
                </button>
                <button
                  type="button"
                  class="chip"
                  aria-pressed={form.distributionFrequency === 'quarterly'}
                  onClick={() => patch({ distributionFrequency: 'quarterly' })}
                >
                  Quartalsweise
                </button>
              </div>
            </div>
          ) : null}

          <NumberField
            label="KESt-Satz"
            unit="%"
            value={form.kestPercent}
            onChange={(v) => patch({ kestPercent: v })}
            min={0}
            max={100}
            step={0.1}
            sliderMax={55}
            sliderStep={0.5}
            hint={showExplanations ? 'Standard: 27,5 %. Nur ändern, wenn du einen abweichenden Satz modellieren willst.' : undefined}
          />
        </div>
      </details>

      <details class="details-section">
        <summary>Rendite &amp; Kosten</summary>
        <div class="section-body">
          <NumberField
            label="Rendite p.a."
            unit="%"
            value={form.annualReturnPercent}
            onChange={(v) => patch({ annualReturnPercent: v })}
            min={-10}
            max={100}
            step={0.1}
            sliderMax={30}
            sliderStep={0.5}
            hint={
              form.fundIsin !== null
                ? 'Vorbelegt aus der Fonds-Historie seit Auflage — nur eine Annahme, keine Prognose. Anpassbar.'
                : undefined
            }
          />

          <NumberField
            label={form.kind === 'acc' ? 'Ertragsrendite (AGE)' : 'Ausschüttungsrendite'}
            unit="%"
            value={form.incomeYieldPercent}
            onChange={(v) => patch({ incomeYieldPercent: v })}
            min={0}
            max={20}
            step={0.1}
            hint={showExplanations ? 'Wird auf den Marktwert am Ereignistag angewendet (Jahresende).' : undefined}
          />

          <NumberField
            label="TER"
            unit="%"
            value={form.terPercent}
            onChange={(v) => patch({ terPercent: v })}
            min={0}
            max={2}
            step={0.01}
          />
        </div>
      </details>
    </div>
  );
}
